import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Report } from "@/services/backend/htz/report/types/report";
import { useHtzReportAddAssessmentMutation } from "@/services/backend/htz/report/api/report-api";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

/**
 * ReportPhotoAddButton adds a photo for one component of the report's air
 * handling unit. The component is the only choice to make, so it is picked
 * from the menu itself: a photo starts without an image, which is uploaded
 * on the card of the photo, see {@link ReportPhotoCard}.
 */
export function ReportPhotoAddButton({
  report,
  disabled,
}: {
  report: Report;
  disabled?: boolean;
}) {
  const [add, { isLoading, error }] = useHtzReportAddAssessmentMutation();

  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: report.ahuDataId ?? "" },
    { skip: !report.ahuDataId },
  );

  const components = [...(ahuData?.components ?? [])].sort(
    (a, b) => a.order - b.order,
  );

  // The component is referenced by its componentId, the stable reference
  // of the component, not by the id of the component data.
  const addFor = (componentId: UUID) => {
    if (isLoading) {
      return;
    }

    add({ id: report.id, type: "photo", assessmentId: v4(), componentId })
      .unwrap()
      .then(() => {
        toast.success(t("Foto hinzugefügt"));
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  // A photo belongs to a component, so there is nothing to add before an
  // air handling unit is assigned.
  if (!report.ahuId) {
    return null;
  }

  return (
    <div className="grid justify-items-end gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" disabled={disabled || isLoading}>
            <Plus />
            {t("Foto")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("Foto hinzufügen für")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {components.length === 0 ? (
            <DropdownMenuItem disabled>
              {t("Die Anlage hat noch keine Komponenten")}
            </DropdownMenuItem>
          ) : (
            components.map((component) => (
              <DropdownMenuItem
                key={component.id}
                onClick={() => addFor(component.componentId)}
              >
                <Plus />
                <span>{component.name}</span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RTKQueryErrorAlert error={error} />
    </div>
  );
}
